/**
 * Renewal Utilities
 *
 * Contracts run a minimum initial term (e.g. 90 days) and then auto-renew
 * monthly. Renewing means appending monthly flights at every level — campaign
 * order (budget + end date), campaign (budget + flight schedule) and every
 * line item (budget + flight schedule) — which is labor-intensive by hand.
 *
 * These pure helpers build that renewal data so callers only have to write
 * the result into the form:
 * - Flight dates continue the cadence: each renewal flight starts the day
 *   after the previous flight ends and runs for one month (start + 1 month
 *   - 1 day), e.g. a last flight of 08/02–09/01 renews as 09/02–10/01.
 * - The campaign's renewal flights each get the last flight's budget; CPM
 *   carries over.
 * - DELIVERY line items (markedForDelivery !== false) get the same renewal
 *   flight dates, each with their OWN last flight's budget — extending their
 *   existing monthly cadence. Line items without flights fall back to a
 *   pro-rata share of the campaign renewal by budgetTotal.
 * - REFERENCE line items (markedForDelivery === false) are the customer-facing
 *   view: they split each campaign renewal flight pro-rata by budgetTotal so
 *   the set keeps accounting for the full campaign budget — each flight is
 *   effectively the customer's monthly bill.
 *
 * Rate changes (renewal with new contract terms) are supported via options:
 * - options.newMonthlyBudget — the new monthly rate (defaults to the current
 *   rate for a same-terms renewal). Delivery line items scale by the same
 *   rate factor.
 * - options.effectiveDate — when set and inside the current last flight, the
 *   flight is SPLIT at that date into old-rate and new-rate day-prorated
 *   segments (mid-flight increase/decrease). On/after the flight end it's a
 *   boundary renewal at the new rate.
 */

import moment from 'moment-timezone';

const FLIGHT_FIELDS = ['flightSchedule', 'flightSchedules'];

/**
 * Return whichever flight field name exists on the entity (the codebase uses
 * both `flightSchedule` and `flightSchedules`), defaulting to `flightSchedule`.
 */
export const getFlightsFieldName = (entity) =>
  FLIGHT_FIELDS.find((field) => Array.isArray(entity?.[field])) || 'flightSchedule';

/**
 * Find the flight with the furthest end date (the cadence anchor).
 */
export const getLastFlight = (flights) => {
  if (!Array.isArray(flights) || flights.length === 0) return null;
  return flights.reduce((latest, flight) => {
    if (!flight?.endDate) return latest;
    if (!latest) return flight;
    return moment(flight.endDate).isAfter(moment(latest.endDate)) ? flight : latest;
  }, null);
};

/**
 * Distribute `totalCents` across `weights` proportionally, in integer cents,
 * so the shares sum exactly to totalCents. Zero total weight splits evenly.
 *
 * @param {number} totalCents – integer cents to distribute
 * @param {Array<number>} weights – relative shares (e.g. line item budgets)
 * @returns {Array<number>} integer cents per weight
 */
export const distributeCentsProRata = (totalCents, weights) => {
  if (!Array.isArray(weights) || weights.length === 0 || !(totalCents > 0)) {
    return (weights || []).map(() => 0);
  }
  const count = weights.length;
  const weightTotal = weights.reduce((sum, w) => sum + (Number(w) || 0), 0);
  const base = weights.map((w) =>
    Math.floor(weightTotal > 0 ? (totalCents * (Number(w) || 0)) / weightTotal : totalCents / count)
  );
  const remainder = totalCents - base.reduce((sum, b) => sum + b, 0);
  return base.map((b, i) => b + (i < remainder ? 1 : 0));
};

/**
 * Split a flight at an effective date for a mid-flight rate change, using
 * daily proration in integer cents: the days before the effective date keep
 * the old rate, the days from the effective date onward get the new rate
 * (a full-month budget prorated by segment days).
 *
 * @param {Object} flight – the flight to split
 * @param {string|Date|moment.Moment} effectiveDate – first day at the new rate
 * @param {number} newBudget – the new full-month budget
 * @param {string} [timezone='UTC']
 * @returns {Array|null} [oldSegment, newSegment] (or a single converted flight
 *          when the effective date is on/before the flight start), or null
 *          when the effective date is after the flight end (boundary renewal,
 *          no split needed)
 */
export const splitFlightAtDate = (flight, effectiveDate, newBudget, timezone = 'UTC') => {
  if (!flight?.startDate || !flight?.endDate || !effectiveDate) return null;
  const flightStart = moment(flight.startDate).tz(timezone).startOf('day');
  const flightEnd = moment(flight.endDate).tz(timezone).startOf('day');
  const effective = moment(effectiveDate).tz(timezone).startOf('day');
  if (!flightStart.isValid() || !flightEnd.isValid() || !effective.isValid()) return null;

  // Boundary renewal (or later): this flight is untouched
  if (effective.isAfter(flightEnd)) return null;

  const totalDays = flightEnd.diff(flightStart, 'days') + 1;
  const oldDays = Math.max(0, effective.diff(flightStart, 'days'));
  const newDays = totalDays - oldDays;
  const oldBudget = Number(flight.budget) || 0;
  const nextBudget = Number(newBudget) || 0;

  // Effective on/before the flight start: the whole flight converts
  if (oldDays === 0) {
    return [{ ...flight, budget: Number(nextBudget.toFixed(2)) }];
  }

  const oldCents = Math.round((oldBudget * 100 * oldDays) / totalDays);
  const newCents = Math.round((nextBudget * 100 * newDays) / totalDays);
  const effectiveStr = effective.format('YYYY-MM-DD');

  return [
    {
      ...flight,
      endDate: moment.tz(effectiveStr, timezone).subtract(1, 'day').endOf('day').utc(),
      budget: oldCents / 100,
    },
    {
      ...flight,
      startDate: moment.tz(effectiveStr, timezone).startOf('day').utc(),
      budget: newCents / 100,
    },
  ];
};

/**
 * Build `months` renewal flights continuing the cadence of the last flight.
 * Dates are computed in the given timezone (matching the date pickers) and
 * stored as UTC start-of-day / end-of-day moments like existing flights.
 *
 * @param {Array} flights – existing flights of the entity being renewed
 * @param {number} months – how many monthly flights to append
 * @param {string} [timezone='UTC']
 * @returns {Array|null} renewal flights, or null when there is nothing valid
 *          to renew from (no flights, invalid dates, or no budget)
 */
export const buildRenewalFlights = (flights, months, timezone = 'UTC') => {
  const count = Math.floor(Number(months));
  const lastFlight = getLastFlight(flights);
  const budget = Number(lastFlight?.budget);
  if (!lastFlight || !count || count < 1) return null;
  if (!lastFlight.startDate || !lastFlight.endDate || !(budget > 0)) return null;

  const renewalFlights = [];
  // Anchor on the calendar day after the last flight ends, in user timezone
  let nextStartStr = moment(lastFlight.endDate).tz(timezone).add(1, 'day').format('YYYY-MM-DD');

  for (let i = 0; i < count; i++) {
    const startDate = moment.tz(nextStartStr, timezone).startOf('day').utc();
    const endDate = moment.tz(nextStartStr, timezone).add(1, 'months').subtract(1, 'day').endOf('day').utc();
    renewalFlights.push({
      startDate,
      endDate,
      budget: Number(budget.toFixed(2)),
      ...(lastFlight.cpm != null ? { cpm: lastFlight.cpm } : {}),
    });
    nextStartStr = moment.tz(nextStartStr, timezone).add(1, 'months').format('YYYY-MM-DD');
  }

  return renewalFlights;
};

/**
 * Apply a renewal to a single campaign object (pure — returns new objects).
 *
 * Appends renewal flights to the campaign, increases its budget, extends its
 * end date (renewal only ever extends). Delivery line items get the same
 * renewal flight dates at each line item's own monthly rate (its last
 * flight's budget); reference line items split the campaign renewal pro-rata
 * so their set keeps covering the full campaign budget.
 *
 * @param {Object} campaign – form-state campaign (budget, endDate, lineItems,
 *        flightSchedule/flightSchedules)
 * @param {number} months
 * @param {string} [timezone='UTC']
 * @returns {null|{campaign: Object, renewalTotal: number, newEndDate: moment.Moment,
 *          allFlights: Array}} null when the campaign has nothing to renew from
 */
export const applyRenewalToCampaign = (campaign, months, timezone = 'UTC', options = {}) => {
  if (!campaign) return null;
  const field = getFlightsFieldName(campaign);
  const flights = Array.isArray(campaign[field]) ? campaign[field] : [];
  const renewalFlightsBase = buildRenewalFlights(flights, months, timezone);
  if (!renewalFlightsBase) return null;

  // Rate change (optional): renewal flights run at the new monthly budget.
  // A mid-flight effective date splits the current flight into old-rate and
  // new-rate day-prorated segments.
  const lastFlight = getLastFlight(flights);
  const oldMonthlyBudget = Number(lastFlight.budget);
  const newMonthlyBudget =
    Number(options.newMonthlyBudget) > 0 ? Number(options.newMonthlyBudget) : oldMonthlyBudget;
  const rateChanged = Math.abs(newMonthlyBudget - oldMonthlyBudget) >= 0.005;
  const rateFactor = oldMonthlyBudget > 0 ? newMonthlyBudget / oldMonthlyBudget : 1;

  const renewalFlights = renewalFlightsBase.map((flight) => ({
    ...flight,
    budget: Number(newMonthlyBudget.toFixed(2)),
  }));

  const splitSegments =
    rateChanged && options.effectiveDate
      ? splitFlightAtDate(lastFlight, options.effectiveDate, newMonthlyBudget, timezone)
      : null;

  const newEndDate = renewalFlights[renewalFlights.length - 1].endDate;
  const renewalTotal = renewalFlights.reduce((sum, f) => sum + f.budget, 0);
  // Budget delta = renewal months, plus the split month's rate-change delta
  // (the segments replace the old last flight's budget).
  const splitDelta = splitSegments
    ? splitSegments.reduce((sum, f) => sum + f.budget, 0) - oldMonthlyBudget
    : 0;
  const campaignBudgetDelta = renewalTotal + splitDelta;

  // New campaign flights either replace the last flight (split) or append
  const baseFlights = splitSegments ? flights.filter((f) => f !== lastFlight) : flights;
  const campaignFlightUpdate = splitSegments
    ? [...baseFlights, ...splitSegments, ...renewalFlights]
    : [...flights, ...renewalFlights];
  // The flights reference items mirror: split segments + renewal months
  const campaignNewFlights = splitSegments ? [...splitSegments, ...renewalFlights] : renewalFlights;

  // Two sets of line items with different renewal semantics:
  // - DELIVERY items (markedForDelivery !== false) mirror their own cadence,
  //   scaled by the same rate factor on a rate change.
  // - REFERENCE items (markedForDelivery === false) split the campaign's new
  //   flights pro-rata so the set keeps covering the full campaign budget.
  const lineItems = Array.isArray(campaign.lineItems) ? campaign.lineItems : [];
  const isDeliverable = (lineItem) => lineItem.markedForDelivery !== false;
  const deliverable = lineItems.filter(isDeliverable);
  const reference = lineItems.filter((li) => !isDeliverable(li));

  // Fallback for delivery line items with no flights of their own: a pro-rata
  // share of each campaign renewal flight by budgetTotal (integer cents).
  const weights = deliverable.map((li) => Number(li.budgetTotal) || 0);
  const perFlightShares = renewalFlights.map((flight) =>
    distributeCentsProRata(Math.round(flight.budget * 100), weights)
  );

  const referenceWeights = reference.map((li) => Number(li.budgetTotal) || 0);
  const referenceShares = campaignNewFlights.map((flight) =>
    distributeCentsProRata(Math.round(flight.budget * 100), referenceWeights)
  );

  const cpmFor = (flight, lineItem) =>
    flight.cpm != null ? { cpm: flight.cpm } : lineItem.cpm != null ? { cpm: lineItem.cpm } : {};

  let deliverableIdx = -1;
  let referenceIdx = -1;
  const updatedLineItems = lineItems.map((lineItem) => {
    const liField = getFlightsFieldName(lineItem);
    const liFlights = Array.isArray(lineItem[liField]) ? lineItem[liField] : [];

    let liBaseFlights = liFlights;
    let liNewFlights;
    let liDelta;

    if (isDeliverable(lineItem)) {
      deliverableIdx += 1;
      const liIdx = deliverableIdx;
      const liLastFlight = getLastFlight(liFlights);
      const ownOldRate = Number(liLastFlight?.budget);

      if (ownOldRate > 0) {
        // Mirror the line item's own cadence, scaled by the rate factor
        const ownNewRate = Number((ownOldRate * rateFactor).toFixed(2));
        const ownSplit =
          rateChanged && options.effectiveDate
            ? splitFlightAtDate(liLastFlight, options.effectiveDate, ownNewRate, timezone)
            : null;

        const ownRenewals = renewalFlights.map((flight) => ({
          startDate: flight.startDate,
          endDate: flight.endDate,
          budget: ownNewRate,
          ...cpmFor(flight, lineItem),
        }));

        if (ownSplit) {
          liBaseFlights = liFlights.filter((f) => f !== liLastFlight);
          liNewFlights = [...ownSplit, ...ownRenewals];
          liDelta =
            ownSplit.reduce((sum, f) => sum + f.budget, 0) -
            ownOldRate +
            ownRenewals.reduce((sum, f) => sum + f.budget, 0);
        } else {
          liNewFlights = ownRenewals;
          liDelta = ownRenewals.reduce((sum, f) => sum + f.budget, 0);
        }
      } else {
        // No cadence of its own: pro-rata share of the campaign renewal
        liNewFlights = renewalFlights.map((flight, fIdx) => ({
          startDate: flight.startDate,
          endDate: flight.endDate,
          budget: perFlightShares[fIdx][liIdx] / 100,
          ...cpmFor(flight, lineItem),
        }));
        liDelta = liNewFlights.reduce((sum, f) => sum + f.budget, 0);
      }
    } else {
      referenceIdx += 1;
      const rIdx = referenceIdx;

      const shared = campaignNewFlights.map((flight, fIdx) => ({
        startDate: flight.startDate,
        endDate: flight.endDate,
        budget: referenceShares[fIdx][rIdx] / 100,
        ...cpmFor(flight, lineItem),
      }));

      if (splitSegments) {
        // The reference item's own last flight is replaced by its share of
        // the split segments (+ renewal months)
        const refLastFlight = getLastFlight(liFlights);
        const refLastBudget = Number(refLastFlight?.budget) || 0;
        liBaseFlights = refLastFlight ? liFlights.filter((f) => f !== refLastFlight) : liFlights;
        liNewFlights = shared;
        liDelta = shared.reduce((sum, f) => sum + f.budget, 0) - refLastBudget;
      } else {
        liNewFlights = shared;
        liDelta = shared.reduce((sum, f) => sum + f.budget, 0);
      }
    }

    const updatedLineItem = {
      ...lineItem,
      budgetTotal: Number(((Number(lineItem.budgetTotal) || 0) + liDelta).toFixed(2)),
      [liField]: [...liBaseFlights, ...liNewFlights],
    };
    // Extend the line item's own end date to cover the renewal (never shortens)
    if (!lineItem.endDate || moment(newEndDate).isAfter(moment(lineItem.endDate))) {
      updatedLineItem.endDate = newEndDate;
    }
    return updatedLineItem;
  });

  const updatedCampaign = {
    ...campaign,
    budget: Number(((Number(campaign.budget) || 0) + campaignBudgetDelta).toFixed(2)),
    [field]: campaignFlightUpdate,
    lineItems: updatedLineItems,
  };
  if (!campaign.endDate || moment(newEndDate).isAfter(moment(campaign.endDate))) {
    updatedCampaign.endDate = newEndDate;
  }

  return {
    campaign: updatedCampaign,
    renewalTotal: Number(campaignBudgetDelta.toFixed(2)),
    newEndDate,
    allFlights: updatedCampaign[field],
    rateChange: rateChanged
      ? {
          oldMonthlyBudget,
          newMonthlyBudget,
          effectiveDate: options.effectiveDate || null,
          split: !!splitSegments,
        }
      : null,
  };
};

/**
 * Apply a renewal to every campaign in a campaign order. Campaigns without a
 * renewable flight schedule are skipped (reported in skippedCount).
 *
 * @param {Array} campaigns
 * @param {number} months
 * @param {string} [timezone='UTC']
 * @returns {null|{campaigns: Array, totalRenewal: number, furthestEndDate: moment.Moment,
 *          renewedCount: number, skippedCount: number}} null when no campaign
 *          could be renewed
 */
export const applyRenewalToCampaigns = (campaigns, months, timezone = 'UTC', options = {}) => {
  if (!Array.isArray(campaigns)) return null;

  let totalRenewal = 0;
  let furthestEndDate = null;
  let renewedCount = 0;
  let skippedCount = 0;
  let rateChange = null;

  const updatedCampaigns = campaigns.map((campaign) => {
    const result = applyRenewalToCampaign(campaign, months, timezone, options);
    if (!result) {
      skippedCount += 1;
      return campaign;
    }
    renewedCount += 1;
    totalRenewal += result.renewalTotal;
    if (!furthestEndDate || moment(result.newEndDate).isAfter(furthestEndDate)) {
      furthestEndDate = result.newEndDate;
    }
    if (!rateChange && result.rateChange) rateChange = result.rateChange;
    return result.campaign;
  });

  if (renewedCount === 0) return null;

  return {
    campaigns: updatedCampaigns,
    totalRenewal: Number(totalRenewal.toFixed(2)),
    furthestEndDate,
    renewedCount,
    skippedCount,
    rateChange,
  };
};
